import { endpoints } from '../config/api';

const WHATS_NEW_KEY = 'fragment_whats_new';

export const defaultWhatsNew = [
    {
        id: 'wn_1',
        title: 'Multi-Calendar Sync & Real-Time Conflict Alerts',
        category: 'Organiser',
        type: 'New Feature',
        platform: 'Web',
        date: 'September 25, 2026',
        actions: '• Consolidated Google, Outlook, and Apple calendar two-way synchronisation.\n• Instant notifications on overlapping bookings.\n• Configurable buffer times between consecutive events.',
        description: 'Seamlessly connect multiple external calendars. Fragment now actively detects scheduling conflicts across your professional and personal appointments.',
        media: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=1200&q=80',
        mediaType: 'image',
        likes: 0,
        dislikes: 0,
        feedbacks: []
    },
    {
        id: 'wn_2',
        title: 'Refined Dark Palette & Fluid Micro-Interactions',
        category: 'Individual',
        type: 'Design',
        platform: 'Mobile',
        date: 'September 22, 2026',
        actions: '• High-contrast dark theme engineered for extended screen time.\n• Tactile micro-animations on completed todos and event toggles.\n• System appearance auto-sync matching device settings.',
        description: 'A reimagined dark mode interface built with deep charcoal tones and crisp typography. Designed to enhance clarity and focus while reducing eye strain.',
        media: '',
        mediaType: 'none',
        likes: 0,
        dislikes: 0,
        feedbacks: []
    },
    {
        id: 'wn_3',
        title: 'Direct RSVP Tracking & Branded Booking Links',
        category: 'Organiser',
        type: 'Improvement',
        platform: 'Web',
        date: 'September 18, 2026',
        actions: '• Shareable RSVP landing pages customized with brand colors and logos.\n• Automated confirmation emails and calendar invitation attachments.\n• Real-time attendee analytics with CSV data export.',
        description: 'Publish public or private event links and track guest responses in real time without requiring attendees to install an application.',
        media: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80',
        mediaType: 'image',
        likes: 0,
        dislikes: 0,
        feedbacks: []
    },
    {
        id: 'wn_4',
        title: 'Offline Mode with Automatic Cloud Synchronization',
        category: 'Individual',
        type: 'Improvement',
        platform: 'Mobile',
        date: 'September 14, 2026',
        actions: '• Complete offline access to past, current, and upcoming agendas.\n• Seamless background queueing for newly drafted tasks and reminders.\n• Zero data conflicts when transitioning between flight mode and cellular networks.',
        description: 'Stay productive regardless of connectivity. All modifications made while offline are saved securely on device and reconciled automatically when online.',
        media: '',
        mediaType: 'none',
        likes: 0,
        dislikes: 0,
        feedbacks: []
    }
];

// Helper to generate candidate endpoints for reliable sync
const getCandidateUrls = (suffix = '') => {
    const primary = endpoints.whatsNew ? (suffix ? (endpoints.whatsNewById ? endpoints.whatsNewById(suffix.replace(/^\//, '')) : `${endpoints.whatsNew()}${suffix}`) : endpoints.whatsNew()) : null;
    const urls = [
        primary,
        'http://localhost:3000/website/admin/whats-new' + suffix,
        'http://localhost:4000/website/admin/whats-new' + suffix
    ].filter(Boolean);
    return Array.from(new Set(urls));
};

// Helper to sanitize items and remove stale fields
const sanitizeWhatsNewItem = (item) => {
    let platform = item.platform || 'Web';
    const lowerP = String(platform).toLowerCase();
    if (lowerP.includes('mobile') || lowerP.includes('ios') || lowerP.includes('android') || lowerP.includes('phone')) {
        platform = 'Mobile';
    } else if (lowerP === 'web') {
        platform = 'Web';
    } else if (platform && platform.trim()) {
        platform = platform.trim();
    } else {
        platform = 'Web';
    }
    const clean = { ...item };
    delete clean.version;
    const isVid = clean.mediaType === 'video' || (clean.media && (
        String(clean.media).startsWith('data:video') ||
        /\.(mp4|webm|mov|ogg|m4v|mkv)($|\?)/i.test(String(clean.media)) ||
        String(clean.media).includes('youtube.com') ||
        String(clean.media).includes('youtu.be') ||
        String(clean.media).includes('vimeo.com') ||
        String(clean.media).includes('loom.com') ||
        String(clean.media).toLowerCase().includes('video')
    ));
    return {
        ...clean,
        platform,
        media: clean.media || '',
        mediaType: clean.media ? (isVid ? 'video' : 'image') : 'none',
        likes: Number(clean.likes) || 0,
        dislikes: Number(clean.dislikes) || 0,
        feedbacks: Array.isArray(clean.feedbacks) ? clean.feedbacks : []
    };
};

export const getWhatsNew = async () => {
    try {
        const candidateUrls = getCandidateUrls('');

        for (const url of candidateUrls) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 15000);
                const response = await fetch(url, { signal: controller.signal });
                clearTimeout(timeoutId);

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data) && data.length > 0) {
                        const sanitized = data.map(sanitizeWhatsNewItem);
                        localStorage.setItem(WHATS_NEW_KEY, JSON.stringify(sanitized));
                        return sanitized;
                    }
                }
            } catch (err) {
                // Try next endpoint
            }
        }

        const stored = localStorage.getItem(WHATS_NEW_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
                const sanitized = parsed.map(sanitizeWhatsNewItem);
                localStorage.setItem(WHATS_NEW_KEY, JSON.stringify(sanitized));
                return sanitized;
            }
        }
        const initial = defaultWhatsNew.map(sanitizeWhatsNewItem);
        localStorage.setItem(WHATS_NEW_KEY, JSON.stringify(initial));
        return initial;
    } catch (error) {
        console.error('Error fetching What\'s New items:', error);
        return defaultWhatsNew.map(sanitizeWhatsNewItem);
    }
};

export const addWhatsNew = async (itemData) => {
    try {
        const candidateUrls = getCandidateUrls('');

        let savedBackend = null;
        let lastError = null;
        for (const url of candidateUrls) {
            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(itemData)
                });
                if (response.ok) {
                    savedBackend = await response.json();
                    break;
                } else {
                    const errData = await response.json().catch(() => null);
                    lastError = errData?.message || `HTTP ${response.status}`;
                }
            } catch (e) {
                lastError = e.message;
            }
        }

        if (!savedBackend) {
            throw new Error(lastError || 'Could not connect to server to save update');
        }

        const current = await getWhatsNew();
        const newItem = sanitizeWhatsNewItem({
            id: savedBackend?._id || savedBackend?.id || `wn_${Date.now()}`,
            _id: savedBackend?._id || savedBackend?.id || `wn_${Date.now()}`,
            title: itemData.title || 'Untitled Update',
            category: itemData.category || 'Organiser',
            type: itemData.type || 'New Feature',
            platform: itemData.platform || 'Web',
            date: itemData.date || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
            actions: itemData.actions || '',
            description: itemData.description || '',
            media: itemData.media || '',
            mediaType: itemData.media ? (itemData.mediaType || 'image') : 'none',
            likes: 0,
            dislikes: 0,
            feedbacks: []
        });

        const updated = [newItem, ...current.filter(it => (it.id || it._id) !== (newItem.id || newItem._id))];
        localStorage.setItem(WHATS_NEW_KEY, JSON.stringify(updated));
        triggerWhatsNewUpdate();
        return savedBackend || newItem;
    } catch (error) {
        console.error('Error adding What\'s New item:', error);
        throw error;
    }
};

const triggerWhatsNewUpdate = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('whatsnew-storage-update'));
        try {
            if (typeof BroadcastChannel !== 'undefined') {
                const ch = new BroadcastChannel('fragment_whatsnew_channel');
                ch.postMessage({ type: 'update' });
                ch.close();
            }
        } catch (e) {}
    }
};

export const updateWhatsNew = async (id, itemData) => {
    try {
        const candidateUrls = getCandidateUrls(`/${id}`);
        let savedBackend = null;
        let lastError = null;

        for (const url of candidateUrls) {
            try {
                const response = await fetch(url, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(itemData)
                });
                if (response.ok) {
                    savedBackend = await response.json();
                    break;
                } else {
                    const errData = await response.json().catch(() => null);
                    lastError = errData?.message || `HTTP ${response.status}`;
                }
            } catch (e) {
                lastError = e.message;
            }
        }

        if (!savedBackend) {
            throw new Error(lastError || 'Could not connect to server to update item');
        }

        const current = await getWhatsNew();
        const updated = current.map(item => 
            (String(item.id) === String(id) || String(item._id) === String(id)) 
                ? sanitizeWhatsNewItem({ ...item, ...itemData, ...(savedBackend || {}) }) 
                : item
        );
        localStorage.setItem(WHATS_NEW_KEY, JSON.stringify(updated));
        triggerWhatsNewUpdate();
        return savedBackend || updated;
    } catch (error) {
        console.error('Error updating What\'s New item:', error);
        throw error;
    }
};

export const deleteWhatsNew = async (id) => {
    try {
        const candidateUrls = getCandidateUrls(`/${id}`);
        for (const url of candidateUrls) {
            try {
                const response = await fetch(url, { method: 'DELETE' });
                if (response.ok) {
                    break;
                }
            } catch (e) {
                // Try next endpoint
            }
        }

        const current = await getWhatsNew();
        const updated = current.filter(item => String(item.id) !== String(id) && String(item._id) !== String(id));
        localStorage.setItem(WHATS_NEW_KEY, JSON.stringify(updated));
        triggerWhatsNewUpdate();
        return updated;
    } catch (error) {
        console.error('Error deleting What\'s New item:', error);
    }
};

export const saveWhatsNewReaction = async (id, newReaction, previousReaction) => {
    let updatedCounts = { likes: 0, dislikes: 0 };
    if (typeof window !== 'undefined') {
        try {
            const cached = localStorage.getItem(WHATS_NEW_KEY);
            let items = cached ? JSON.parse(cached) : defaultWhatsNew;
            items = items.map((it) => {
                const isMatch = String(it.id) === String(id) || String(it._id) === String(id);
                if (isMatch) {
                    let likes = it.likes || 0;
                    let dislikes = it.dislikes || 0;
                    if (previousReaction === 'like' && likes > 0) likes -= 1;
                    if (previousReaction === 'dislike' && dislikes > 0) dislikes -= 1;
                    if (newReaction === 'like') likes += 1;
                    if (newReaction === 'dislike') dislikes += 1;
                    updatedCounts = { likes, dislikes };
                    return { ...it, likes, dislikes };
                }
                return it;
            });
            localStorage.setItem(WHATS_NEW_KEY, JSON.stringify(items));
            triggerWhatsNewUpdate();
        } catch (e) {}
    }
    return updatedCounts;
};

export const saveWhatsNewFeedback = async (id, feedbackData) => {
    const text = typeof feedbackData === 'string' ? feedbackData : feedbackData.text;
    const email = typeof feedbackData === 'object' ? feedbackData.email || '' : '';
    const newFeedback = {
        id: `fb_${Date.now()}`,
        itemId: id,
        text,
        email,
        date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
        createdAt: new Date().toISOString()
    };

    if (typeof window !== 'undefined') {
        try {
            const existingFb = JSON.parse(localStorage.getItem('fragment_whatsnew_feedbacks') || '[]');
            existingFb.push(newFeedback);
            localStorage.setItem('fragment_whatsnew_feedbacks', JSON.stringify(existingFb));

            const cached = localStorage.getItem(WHATS_NEW_KEY);
            if (cached) {
                let items = JSON.parse(cached);
                items = items.map((it) => {
                    const isMatch = String(it.id) === String(id) || String(it._id) === String(id);
                    if (isMatch) {
                        const feedbacks = Array.isArray(it.feedbacks) ? [...it.feedbacks, newFeedback] : [newFeedback];
                        return { ...it, feedbacks };
                    }
                    return it;
                });
                localStorage.setItem(WHATS_NEW_KEY, JSON.stringify(items));
                triggerWhatsNewUpdate();
            }
        } catch (e) {}
    }
    return { success: true, feedback: newFeedback };
};
